// This file is part of re-motion
// Copyright (c) rubicon IT GmbH, www.rubicon.eu
// 
export type Listener<T> = {
  addListener(callback: T): void;
  removeListener(callback: T): void;
};

/**
 * Describes the Browser Extension in a browser-independent ways.
 * Since browsers might have different API implementations, these differences are polyfilled to this API surface.
 * @see [MDN](https://developer.mozilla.org/en-US/docs/Mozilla/Add-ons/WebExtensions/API)
 */
export type BrowserApi = {
  browserAction: {
    disable(tabId?: number): Promise<void>;
    enable(tabId?: number): Promise<void>;
    setBadgeBackgroundColor(details: {
      color: ColorArray,
      tabId?: number
    }): Promise<void>;
    setBadgeText(details: {
      text: Nullable<string>,
      tabId?: number
    }): Promise<void>;
    setBadgeTextColor(details: {
      color: ColorArray,
      tabId?: number
    }): Promise<void>;
    setIcon(details: {
      imageData?: ImageData | { [key: number]: ImageData };
      path?: string | { [key: number]: string };
      tabId?: number;
    }): Promise<void>
    setTitle(details: {
      title: Nullable<string>,
      tabId?: number
    }): Promise<void>;
  };
  
  cookies: {
    /**
     * Requires 'cookies' permission.
     */
    getAll(details: {
      url?: string;
      storeId?: string;
    }): Promise<Cookie[]>;
    /**
     * Requires 'cookies' permission.
     */
    getAllCookieStores(): Promise<CookieStore[]>;
  };

  runtime: {
    readonly lastError: Nullable<any>;
    readonly id: string;

    onConnect: Listener<(port: Port) => void>;
    onMessage: Listener<(message: object, sender: MessageSender) => Promise<any>>;

    connect(extensionID?: string, connectInfo?: {
      name?: string
    }): Port;
    /**
     * Requires 'nativeMessaging' permission.
     */
    connectNative(application: string): Port;
    getManifest(): Manifest;
    sendMessage(message: any): Promise<any>;
  };

  tabs: {
    executeScript(tabID: number, details: {
      allFrames?: boolean;
      file?: string;
      frameId?: number;
      runAt?: "document_start" | "document_end" | "document_idle";
    }): Promise<any[]>;
    getTab(tabID: number): Promise<Tab>;
    query(queryOptions: { url: string } ): Promise<Tab[]>;
    reload(id: number): void;
  };

  windows: {
    get(id: number): Promise<Window>;
    update(id: number, updateOptions: { focused: boolean, drawAttention: boolean }): void;
  };

  storage: {
    local: Storage,
    managed: ReadOnlyStorage,
  };
  
  alarms: {
    create(name: string, alarmInfo: {delayInMinutes?: number, periodInMinutes?: number, when?: number}, callback?: Function): Promise<any>;
    onAlarm: Listener<(alarm: { name: string, periodInMinutes?: number, scheduledTime: number})=>void>;
  }
};

/**
 * Represents a color using RGBA.
 * @see [MDN](https://developer.mozilla.org/en-US/docs/Mozilla/Add-ons/WebExtensions/API/browserAction/ColorArray)
 */
export type ColorArray = [number, number, number, number];

/**
 * Represents information about a HTTP cookie.
 * @see [MDN](https://developer.mozilla.org/en-US/docs/Mozilla/Add-ons/WebExtensions/API/cookies/Cookie)
 */
export type Cookie = {
  name: string;
  value: string;
};

/**
 * Represents a cookie store in the browser.
 * @see [MDN](https://developer.mozilla.org/en-US/docs/Mozilla/Add-ons/WebExtensions/API/cookies/CookieStore)
 */
export type CookieStore = {
  id: string;
  incognito: boolean;
  tabIds: number[];
};

export type Manifest = {
  name: string;
  version: string;
};

/**
 * Represents an object containing information about the sender of a message or connection request.
 * @see [MDN](https://developer.mozilla.org/en-US/docs/Mozilla/Add-ons/WebExtensions/API/runtime/MessageSender)
 */
export type MessageSender = {
  frameId?: number;
  id?: string;
  tab?: Tab;
  url?: string;
};

/**
 * Represents one end of a connection between two specific contexts, which can be used to exchange messages.
 * @see [MDN](https://developer.mozilla.org/en-US/docs/Mozilla/Add-ons/WebExtensions/API/runtime/Port)
 */
export type Port = {
  error?: Error;
  name: string;
  disconnect(): void;
  onDisconnect: Listener<(port: Port) => void>,
  onMessage: Listener<(message: object) => void>,
  postMessage(message: object): void;
  sender?: MessageSender;
};

/**
 * Represents an object containing information about a browser tab
 * @see [MDN](https://developer.mozilla.org/en-US/docs/Mozilla/Add-ons/WebExtensions/API/tabs/Tab)
 */
export type Tab = {
  id?: number;
  windowId: number;
};

export type Storage = {
  get(key: string): Promise<any>;
  set(items: {[key: string]: any}): Promise<void>;
  remove(keys: string): Promise<void>;
};

export type ReadOnlyStorage = {
  get<T>(key: string): Promise<T|null>;
};